import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import CloseCircle from 'assets/icons/CloseCircle';
import COLOR from 'constants/color';
import { KEY_BOARD, TAB_INDEX } from 'constants/general';
import { MODAL_TITLE_LAYOUT } from 'enums';
import { useDialogFocus } from 'hooks/useDialogFocus';
import BaseButton from './BaseButton';

type ModalSize = 'small' | 'medium' | 'large' | 'xlarge';
type ModalVariant = 'default' | 'withHeader' | 'withTabs';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  className?: string;
  overlayClassName?: string;
  zIndex?: string;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  /** id of the element naming the dialog (wires `aria-labelledby`). */
  ariaLabelledById?: string;
}

interface ModalHeaderProps {
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  className?: string;
  children?: ReactNode;
  /** Actions rendered before the close button (e.g. primary header CTA). */
  actions?: ReactNode;
  titleLayout?: MODAL_TITLE_LAYOUT;
  /** id for the title, so a Modal can reference it via `ariaLabelledById`. */
  titleId?: string;
}

interface ModalContentProps {
  children: ReactNode;
  className?: string;
}

interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

const ModalOverlay = ({
  children,
  isOpen,
  onClose,
  closeOnOverlayClick = true,
  overlayClassName = '',
  zIndex = 'z-50'
}: {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  closeOnOverlayClick?: boolean;
  overlayClassName?: string;
  zIndex?: string;
}) => {
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === KEY_BOARD.ESCAPE.KEY && closeOnOverlayClick) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 bg-gray-600/70 dark:bg-secondary-dark-50/80 ${zIndex} ${overlayClassName}`}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role='presentation'
      tabIndex={-1}>
      {children}
    </div>
  );
};

export const ModalHeader = ({
  title,
  subtitle,
  onClose,
  className = '',
  children,
  actions,
  titleLayout = MODAL_TITLE_LAYOUT.STACK,
  titleId
}: ModalHeaderProps) => {
  const isInlineTitle = titleLayout === MODAL_TITLE_LAYOUT.INLINE;

  return (
    <div
      className={`w-full flex items-center justify-between ~gap-2/3 ~px-4/6 ~py-3/4 bg-primary-400 dark:bg-primary-dark-50 rounded-t-lg ${className}`}>
      <div className='flex-1 min-w-0'>
        <div
          className={
            isInlineTitle
              ? 'flex flex-wrap items-baseline ~gap-x-2/3 ~gap-y-0.5/1 min-w-0'
              : undefined
          }>
          {title && (
            <h3
              id={titleId}
              className='font-medium ~text-base/lg text-black dark:text-secondary-dark-900 truncate'>
              {title}
            </h3>
          )}
          {subtitle && (
            <p
              className={`~text-xs/sm text-secondary-dark-400 dark:text-secondary-600 ${
                isInlineTitle ? 'truncate' : 'mt-0.5'
              }`}>
              {subtitle}
            </p>
          )}
        </div>
        {children}
      </div>
      {actions || onClose ? (
        <div className='flex items-center ~gap-2/4 shrink-0'>
          {actions}
          {onClose ? (
            <BaseButton
              icon={
                <CloseCircle
                  styles='~w-4/6 h-auto rounded-full'
                  fill={COLOR.PRIMARY.LIGHT_900}
                  fillDarkMode={COLOR.PRIMARY.DARK_500}
                />
              }
              onClick={onClose}
              variant='secondary'
              aria-label='Close modal'
              size='xs'
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const ModalContent = ({
  children,
  className = ''
}: ModalContentProps) => {
  return (
    <div
      className={`flex-1 overflow-y-auto scrollbar-thin-custom ~px-1.5/3 ~py-3/6 ${className}`}>
      {children}
    </div>
  );
};

export const ModalFooter = ({ children, className = '' }: ModalFooterProps) => {
  return (
    <div
      className={`w-full flex shrink-0 items-center justify-end ~gap-4/8 ~px-4/6 ~py-3/4 border-t border-gray-200 dark:border-secondary-dark-500 rounded-b-lg ${className}`}>
      {children}
    </div>
  );
};

const Modal = ({
  isOpen,
  onClose,
  children,
  size = 'medium',
  className = '',
  overlayClassName = '',
  zIndex = 'z-[70]',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  ariaLabelledById
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useDialogFocus({ isOpen, containerRef: modalRef });

  const sizeClasses = {
    small: 'w-[90%] sm:w-[80%] md:w-[60%] lg:w-[50%]',
    medium: 'w-[90%] sm:w-[85%] md:w-[75%] lg:w-[65%] xl:w-[55%]',
    large: 'w-[95%] sm:w-[90%] md:w-[85%] lg:w-[80%] xl:w-[70%]',
    xlarge: 'w-[95%] sm:w-[95%] md:w-[90%] lg:w-[85%] xl:w-[80%]'
  };

  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return;
    }

    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === KEY_BOARD.ESCAPE.KEY) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeOnEscape, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <ModalOverlay
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={closeOnOverlayClick}
      overlayClassName={overlayClassName}
      zIndex={zIndex}>
      <div
        ref={modalRef}
        className={`relative top-1/2 -translate-y-1/2 ${sizeClasses[size]} max-h-[min(90vh,90dvh)] mx-auto bg-white dark:bg-secondary-dark-200 rounded-lg shadow-md shadow-primary-600 dark:shadow-primary-900 flex flex-col min-h-0 overflow-hidden animate-fadeIn ${className}`}
        role='dialog'
        aria-modal='true'
        aria-labelledby={ariaLabelledById}
        tabIndex={TAB_INDEX.SKIP}>
        {children}
      </div>
    </ModalOverlay>,
    document.body
  );
};

export default Modal;
